export const getEasternDate = () => {
  const now = new Date();
  const options = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour12: false,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  // Format the current time to Eastern Time parts
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(now);

  // Reconstruct a Date object using the Eastern Time parts
  const year = parseInt(parts.find((part) => part.type === 'year').value, 10);
  const month = parseInt(parts.find((part) => part.type === 'month').value, 10) - 1;
  const day = parseInt(parts.find((part) => part.type === 'day').value, 10);
  const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10);
  const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);
  const second = parseInt(parts.find((part) => part.type === 'second').value, 10);

  // Create a new Date object set to Eastern Time
  return {
    date: new Date(year, month, day, hour, minute, second),
    hour,
  };
};

export const calculateDateDifference = (date) => {
  const reviewDate = new Date(date);
  const now = new Date();
  const diff = now - reviewDate;
  const diffInSeconds = Math.floor(diff / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  } else if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  } else if (diffInWeeks > 0) {
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  } else if (diffInDays > 0) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  } else {
    return `${diffInSeconds} second${diffInSeconds > 1 ? 's' : ''} ago`;
  }
};

/**
 * Format a date object to a string in the format 'dd-mm-yyyy' by default.
 * @param {object} options
 * @param {Date} [options.date] - The date object to format.
 * @param {string} [options.separator] - The separator to use between date parts.
 * @returns {string} The formatted date string.
 */
export const formatDate = ({ date, separator = '-' }) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return [month, day, year].join(separator);
};

/**
 * Get the name of the month for a given date.
 * @param {Date} date
 * @returns {string}
 */
export const getLongDate = (date) => {
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month} ${day}, ${year}`;
};

/**
 * Get the name of the weekday for a given date.
 * @param {Date} date
 * @returns {string}
 */
export const getWeekdayName = (date) => {
  const optionsWeekday = { weekday: 'long' };
  const formatterWeekday = new Intl.DateTimeFormat('en-US', optionsWeekday);
  return formatterWeekday.format(date);
};

/**
 * Get the date in the format 'dd/mm'.
 * @param {Date} date
 * @returns {string}
 */
export const getMonthSlashDay = (date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return month + '/' + day;
};

/**
 * Get the list of dates from a comma-separated string.
 * @param {Date} date
 * @returns {string}
 */
export const CSVToArray = (datesString) => {
  if (!datesString) return [];
  return datesString.split(',').map((date) => date.trim());
};

/**
 * Check if a date is blocked.
 * @param {Date} date
 * @param {string[]} globalBlockOutDates
 * @param {string[]} productBlockOutDates
 * @param {string[]} repeatingDisabledDates
 * @returns {boolean}
 */
export const isBlockedDate = (date, globalBlockOutDates, productBlockOutDates, repeatingDisabledDates) => {
  const formattedDate = formatDate({ date });
  const easternTodaysDate = getEasternDate().date;
  const easternCurrentHour = getEasternDate().hour;
  const cutoffTime = window.Boarderie.cutoffTime;

  // Get the date for the next day
  const easternNextDate = getEasternDate().date;
  easternNextDate.setDate(easternNextDate.getDate() + 1);

  // Cutoff Validations
  let monthChanged = false;
  if (easternNextDate.getMonth() !== easternTodaysDate.getMonth()) {
    monthChanged = true;
  }

  let yearChanged = false;
  if (easternNextDate.getFullYear() !== easternTodaysDate.getFullYear()) {
    yearChanged = true;
  }

  const passedCutoffTime = easternCurrentHour >= cutoffTime;
  const validateCutoffTimeDate = date.getDate() === easternNextDate.getDate();

  let validateCutoffTimeMonth = date.getMonth() === easternTodaysDate.getMonth();
  if (monthChanged) {
    validateCutoffTimeMonth = date.getMonth() === easternNextDate.getMonth();
  }

  let validateCutoffTimeYear = date.getFullYear() === easternTodaysDate.getFullYear();
  if (yearChanged) {
    validateCutoffTimeYear = date.getFullYear() === easternNextDate.getFullYear();
  }

  const isBlockedChecks = [
    { condition: 'global', result: globalBlockOutDates.includes(formattedDate) },
    { condition: 'product', result: productBlockOutDates.includes(formattedDate) },
    { condition: 'repeating', result: repeatingDisabledDates.includes(getWeekdayName(date)) },
    { condition: 'beforeToday', result: date <= easternTodaysDate },
    {
      condition: 'afterCutoff',
      result: passedCutoffTime && validateCutoffTimeDate && validateCutoffTimeMonth && validateCutoffTimeYear,
    },
  ];

  // Check if the date is always enabled
  const overrideBlockedDates = CSVToArray(window.Boarderie?.overrideBlockoutDates);
  if (
    overrideBlockedDates.length > 0 &&
    overrideBlockedDates.includes(formattedDate) &&
    !productBlockOutDates.includes(formattedDate)
  ) {
    return {
      isBlocked: false,
      blockedConditions: [],
    };
  }

  const blockedConditions = isBlockedChecks.filter((check) => check.result).map((check) => check.condition);
  return {
    isBlocked: blockedConditions.length > 0,
    blockedConditions: blockedConditions,
  };
};

/**
 * Format a date object to a string in the format 'yyyy-mm-dd'.
 * @param {Date} date - The date object to format.
 * @returns {string} The formatted date string.
 */
export const formatGiftCardDate = (date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return [year, month, day].join('-');
};

export const isPrivatePreorderDate = (date) => {
  const isPreorderSession = sessionStorage.getItem('preorder');
  const isPreorderEnabled = window.Boarderie?.privatePreorder?.enabled;

  if (!isPreorderSession || !isPreorderEnabled) return false;

  const formattedDate = formatDate({ date });
  const preorderDates = window.Boarderie?.privatePreorder?.dates
    ? CSVToArray(window.Boarderie?.privatePreorder?.dates)
    : [];

  if (preorderDates.includes(formattedDate)) {
    return true;
  }
};

export const isGeneralPreorderDate = (date) => {
  const isPrivatePreorderSession = sessionStorage.getItem('preorder');
  const isPreorderEnabled = window.Boarderie?.generalPreorder?.enabled;

  if (!isPreorderEnabled || isPrivatePreorderSession) return false;

  const formattedDate = formatDate({ date });
  const preorderDates = window.Boarderie?.generalPreorder?.dates
    ? CSVToArray(window.Boarderie?.generalPreorder?.dates)
    : [];

  if (preorderDates.includes(formattedDate)) {
    return true;
  }
};
